import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { buildOrizonte12Pulse } from "@/lib/orizonte-12";
import { Orizonte12Banner } from "@/components/Orizonte12Banner";

const DISMISS_KEY = "orizonte:announcement:1.2";

export const Orizonte12Announcement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptions } = useSubscriptions();
  const pulse = useMemo(() => buildOrizonte12Pulse(subscriptions), [subscriptions]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (location.pathname === "/auth" || location.pathname === "/onboarding") return;
    if (window.localStorage.getItem(DISMISS_KEY) === "dismissed") return;

    const timeout = window.setTimeout(() => setIsOpen(true), 900);
    return () => window.clearTimeout(timeout);
  }, [location.pathname]);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, "dismissed");
    setIsOpen(false);
  };

  const openPulse = () => {
    dismiss();
    navigate("/intelligence");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="announcement-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={dismiss}
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        >
          <motion.div
            key="announcement-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Orizonte 1.2 update"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl max-h-[90dvh] overflow-y-auto rounded-2xl border border-orizonte-lime/25 bg-[#020403] shadow-2xl shadow-black/70"
          >
            <button
              type="button"
              onClick={dismiss}
              className="absolute right-4 top-4 z-20 rounded-xl border border-white/10 bg-black/45 p-2 text-orizonte-textMuted backdrop-blur transition-colors hover:text-white"
              aria-label="Close Orizonte 1.2 announcement"
            >
              <X className="h-4 w-4" />
            </button>
            <Orizonte12Banner pulse={pulse} />
            <div className="flex flex-col gap-3 border-t border-white/10 bg-white/[0.02] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-relaxed text-orizonte-textMuted">
                Pulse is live in your dashboard, intelligence, settings, security, and mobile install flow.
              </p>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={dismiss}
                  className="rounded-xl border border-white/10 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-orizonte-textMuted transition-colors hover:bg-white/[0.08] hover:text-white"
                >
                  Later
                </button>
                <button
                  type="button"
                  onClick={openPulse}
                  className="inline-flex items-center gap-2 rounded-xl bg-orizonte-lime px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-orizonte-lime/90"
                >
                  Explore Pulse
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
